import { NavLink } from "react-router-dom";

// `short` is the phone label. "Air Quality" does not fit a fifth of a 375px
// viewport at a legible size, and a tab bar with one wrapped label and four
// straight ones looks broken rather than dense.
const items = [
  { to: "/", label: "Globe", short: "Globe", glyph: "◉" },
  { to: "/air-quality", label: "Air Quality", short: "Air", glyph: "≋" },
  { to: "/preparedness", label: "Prepare", short: "Prepare", glyph: "▲" },
  { to: "/climate", label: "Climate", short: "Climate", glyph: "∿" },
  { to: "/about", label: "About", short: "About", glyph: "i" },
] as const;

export function LeftRail() {
  return (
    <nav
      aria-label="Primary navigation"
      className="rail-nav"
      style={{
        height: "100%",
        background: "var(--color-bg-1)",
        borderRight: "1px solid var(--color-stroke)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingTop: 16,
        gap: 4,
      }}
    >
      <div
        aria-hidden
        className="rail-logo"
        style={{
          width: 40,
          height: 40,
          display: "grid",
          placeItems: "center",
          fontFamily: "var(--font-display)",
          fontWeight: 800,
          fontSize: 20,
          color: "var(--color-ember-500)",
          letterSpacing: "-0.04em",
          marginBottom: 16,
        }}
      >
        WF
      </div>
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.to === "/"}
          aria-label={it.label}
          className="rail-link"
          style={({ isActive }) => ({
            position: "relative",
            width: 40,
            height: 40,
            display: "grid",
            placeItems: "center",
            borderRadius: 10,
            color: isActive ? "var(--color-ember-400)" : "var(--color-text-mid)",
            background: isActive ? "var(--color-bg-3)" : "transparent",
            transition: "background 200ms, color 200ms",
            fontFamily: "var(--font-data)",
            fontSize: 16,
            textDecoration: "none",
          })}
        >
          {({ isActive }) => (
            <>
              <span aria-hidden>{it.glyph}</span>
              {/* Hidden on desktop, where the rail is icon-only and the
                  accessible name comes from aria-label. On a phone an icon
                  strip with no words is a guessing game. */}
              <span aria-hidden className="rail-label">
                {it.short}
              </span>
              {isActive && (
                <span
                  aria-hidden
                  className="rail-active"
                  style={{
                    position: "absolute",
                    left: -16,
                    top: 8,
                    bottom: 8,
                    width: 2,
                    background: "var(--color-ember-500)",
                    boxShadow: "var(--glow-ember)",
                    borderRadius: 2,
                  }}
                />
              )}
            </>
          )}
        </NavLink>
      ))}

      <style>{`
        .rail-label { display: none; }

        /* ── Phone and small tablet: the rail becomes a bottom tab bar ──
           AppShell moves this element into the bottom grid row; these rules
           turn its internals from a vertical strip into a row of five equal
           targets. Each tab is a full flex column so the whole cell is
           tappable, not just the 40px glyph box.

           The active marker flips from a left-edge bar to a top-edge one,
           because on a bottom bar a left bar reads as belonging to the tab
           beside it. */
        @media (max-width: 768px) {
          .rail-nav {
            flex-direction: row !important;
            align-items: stretch !important;
            justify-content: space-around !important;
            padding-top: 0 !important;
            padding-bottom: env(safe-area-inset-bottom, 0px) !important;
            gap: 0 !important;
            border-right: none !important;
            border-top: 1px solid var(--color-stroke) !important;
          }

          .rail-logo { display: none !important; }

          .rail-link {
            width: auto !important;
            height: auto !important;
            flex: 1 1 0 !important;
            grid-auto-flow: row !important;
            align-content: center !important;
            gap: 3px !important;
            padding: 7px 2px !important;
            border-radius: 0 !important;
            background: transparent !important;
            min-width: 0 !important;
          }

          .rail-label {
            display: block !important;
            font-family: var(--font-data);
            font-size: 9px;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            line-height: 1;
          }

          .rail-active {
            left: 0 !important;
            right: 0 !important;
            top: 0 !important;
            bottom: auto !important;
            width: auto !important;
            height: 2px !important;
            border-radius: 0 !important;
          }
        }
      `}</style>
    </nav>
  );
}
